import React from 'react';
import { Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';

const base =
  'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843] focus:border-transparent';

export const Field = ({ label, value, onChange, type = 'text', hint, testId, ...rest }) => (
  <label className="block">
    <span className="block text-xs font-semibold text-gray-600 mb-1.5">{label}</span>
    <input
      type={type}
      value={value ?? ''}
      onChange={(e) => onChange(type === 'number' ? Number(e.target.value) : e.target.value)}
      data-testid={testId}
      className={base}
      {...rest}
    />
    {hint && <span className="block text-[11px] text-gray-400 mt-1">{hint}</span>}
  </label>
);

export const Area = ({ label, value, onChange, rows = 3, testId }) => (
  <label className="block">
    <span className="block text-xs font-semibold text-gray-600 mb-1.5">{label}</span>
    <textarea
      rows={rows}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className={`${base} resize-y`}
    />
  </label>
);

export const Toggle = ({ label, checked, onChange, testId }) => (
  <label className="inline-flex items-center gap-2 cursor-pointer">
    <input
      type="checkbox"
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
      data-testid={testId}
      className="w-4 h-4 accent-[#3D4A26]"
    />
    <span className="text-sm font-medium text-gray-700">{label}</span>
  </label>
);

/** Editable list of plain strings. */
export const StringList = ({ label, items = [], onChange, placeholder = '', testId }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs font-semibold text-gray-600">{label}</span>
      <button
        type="button"
        onClick={() => onChange([...items, ''])}
        data-testid={`${testId}-add`}
        className="inline-flex items-center gap-1 text-xs font-semibold text-[#3D4A26] hover:underline"
      >
        <Plus size={13} /> Ekle
      </button>
    </div>
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            value={it}
            placeholder={placeholder}
            onChange={(e) => onChange(items.map((v, j) => (j === i ? e.target.value : v)))}
            data-testid={`${testId}-${i}`}
            className={base}
          />
          <button
            type="button"
            onClick={() => onChange(items.filter((_, j) => j !== i))}
            data-testid={`${testId}-remove-${i}`}
            className="p-2 text-gray-400 hover:text-red-500"
            title="Kaldır"
          >
            <Trash2 size={15} />
          </button>
        </div>
      ))}
      {items.length === 0 && <p className="text-xs text-gray-400">Henüz kayıt yok.</p>}
    </div>
  </div>
);

/** Editable list of objects; `render(item, patch)` draws the fields. */
export const Repeater = ({ label, items = [], onChange, blank, render, titleOf, testId }) => {
  const patchAt = (i) => (changes) =>
    onChange(items.map((v, j) => (j === i ? { ...v, ...changes } : v)));
  const move = (i, dir) => {
    const next = [...items];
    const t = next[i + dir];
    if (!t) return;
    next[i + dir] = next[i];
    next[i] = t;
    onChange(next);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-bold text-[#1B1B1B]">{label}</span>
        <button
          type="button"
          onClick={() => onChange([...items, { ...blank }])}
          data-testid={`${testId}-add`}
          className="inline-flex items-center gap-1 text-xs font-semibold text-[#3D4A26] hover:underline"
        >
          <Plus size={13} /> Yeni Ekle
        </button>
      </div>
      <div className="space-y-4">
        {items.map((it, i) => (
          <div key={i} className="border border-gray-200 rounded-xl p-4 bg-gray-50/60" data-testid={`${testId}-item-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold uppercase tracking-wider text-[#3D4A26]">
                {titleOf ? titleOf(it, i) : `#${i + 1}`}
              </p>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => move(i, -1)} className="p-1.5 text-gray-400 hover:text-[#3D4A26]" title="Yukarı">
                  <ArrowUp size={14} />
                </button>
                <button type="button" onClick={() => move(i, 1)} className="p-1.5 text-gray-400 hover:text-[#3D4A26]" title="Aşağı">
                  <ArrowDown size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => onChange(items.filter((_, j) => j !== i))}
                  data-testid={`${testId}-remove-${i}`}
                  className="p-1.5 text-gray-400 hover:text-red-500"
                  title="Kaldır"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
            {render(it, patchAt(i), i)}
          </div>
        ))}
        {items.length === 0 && <p className="text-xs text-gray-400">Henüz kayıt yok.</p>}
      </div>
    </div>
  );
};
